import { AfterViewInit, Directive, ElementRef, Input, OnDestroy, Renderer2 } from '@angular/core';
import { fromEvent, mergeWith, Observable, of, Subject, throttleTime } from 'rxjs';
import { map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { v1 as generateUUID } from 'uuid';

@Directive({
  selector: '[appGxSnowfall]',
  standalone: true
})
export class GxSnowfallDirective implements OnDestroy, AfterViewInit {

  // Different snowflake symbols for variety
  @Input() snowflakeSymbols = ['❄', '❅', '❆'];
  // Configuration for snowfall effect
  @Input() enable = false;
  @Input() snowflakeColor: string;
  @Input() fontSizeMin = 0.8; // em
  @Input() fontSizeMax = 2; // em
  @Input() opacityMin = 0.5 // 0-1
  @Input() opacityMax = 1 // 0-1
  @Input() animationDurationMin = 15; // s
  @Input() animationDurationMax = 45; // s
  @Input() animationDelayMin = 0; // s
  @Input() animationDelayMax = 5; // s
  @Input() horizontalDriftMin = -15; // vw
  @Input() horizontalDriftMax = 15; // vw
  @Input() rotationMin = -360; // deg
  @Input() rotationMax = 360; // deg
  // Below 2 effects look good if there's a dark background and the snowflakes have a light color
  @Input() applyFlareEffect = true;
  @Input() applyBlurEffect = true;
  // The amount of snowflakes falling every second, i.e. snowflake frequency.
  @Input() snowfallDensity = 10; // s
  // The amount of inactivity time in minutes it takes to start snowing.
  @Input() inactivityTime = 5 // min

  // Default mode with a darkblue overlay and white snowflakes with flare and blur effect
  @Input() showOverlay = true;

  private snowflakeIntervalRef: NodeJS.Timeout;
  private inactivityTimeoutRef: NodeJS.Timeout;

  // Snowflake event listeners 'should' be garbage-collected when the object is removed from DOM,
  // https://stackoverflow.com/questions/18986520/does-removing-an-element-also-remove-its-event-listeners
  // but let's make extra sure and run the 'unlistener' function before removing it.
  private elStore = new Map<number, {el: HTMLElement, unlistener?: () => void}>;

  // We want to stop snowfall when user switches to another tab and also stop any scheduled snowfall
  // as it can happen that the user switches tabs as part of their workflow and if it started
  // to snow in the 'parked' tab, it might become irritating after a while
  // This way, the animation only plays after inactivityTime if the tab is active and stops
  // when user switches to another tab. When they switch back, the inactivity timer will be restarted.
  private visibilityChange$: Observable<Event> = fromEvent(document, 'visibilitychange');
  private componentDestroyed = new Subject<void>();

  constructor(private elRef: ElementRef,
              private renderer: Renderer2) {
  }

  ngAfterViewInit() {
    if (!this.enable) {
      return;
    }
    // Start the 'user inactivity listener'
    this.visibilityChange$.pipe(
        startWith(null), // kick off without visibilityChange$ having to emit an update
        switchMap(() => document.hidden ?
                of(false) : // emit false if tab is inactive, i.e. restart = false
                fromEvent(window, 'click') // emit true on user input, i.e. restart = true
                    .pipe(
                        startWith(null), // kick off the inner listener without any user input
                        mergeWith(
                            fromEvent(window, 'keydown'),
                            fromEvent(window, 'mousemove'),
                            fromEvent(window, 'wheel')),
                        map(() => true),
                        throttleTime(1000), // stop animation immediately but then don't listen for a sec.
                        takeUntil(this.componentDestroyed))
        )
    ).subscribe((restart) => restart ?
        this.restartSnowfall() :
        this.cleanup()
    );

    // add some default colors
    if (this.showOverlay) {
      this.snowflakeColor = this.snowflakeColor ? this.snowflakeColor : 'white';
    } else this.snowflakeColor = this.snowflakeColor ? this.snowflakeColor : '#8cb4cc';
  }

  ngOnDestroy() {
    this.componentDestroyed.next();
    this.cleanup();
  }

  /**
   * Function to create a single snowflake element
   * @private
   */
  private createSnowflake() {
    const snowflake = this.renderer.createElement('div');
    const snowflakeID = generateUUID();
    const snowflakeText = this.renderer
        .createText(this.snowflakeSymbols[Math.floor(Math.random() * this.snowflakeSymbols.length)]);

    this.renderer.appendChild(snowflake, snowflakeText);
    this.renderer.addClass(snowflake, 'snowflake');

    this.renderer.setStyle(snowflake, 'color', this.snowflakeColor);
    this.renderer.setStyle(snowflake, 'fontSize',
        `${this.random(this.fontSizeMin, this.fontSizeMax)}em`);
    this.renderer.setStyle(snowflake, 'opacity',
        `${this.random(this.opacityMin, this.opacityMax)}`);
    this.renderer.setStyle(snowflake, 'left', `${this.random(0, 100)}vw`);

    // Animation properties for falling and drifting
    this.renderer.setStyle(snowflake, 'animationDuration',
        `${this.random(this.animationDurationMin, this.animationDurationMax)}s`);
    this.renderer.setStyle(snowflake, 'animationDelay',
        `${this.random(this.animationDelayMin, this.animationDelayMax)}s`);
    // Slight horizontal drift
    this.renderer.setStyle(snowflake, '--horizontal-drift',
        `${this.random(this.horizontalDriftMin, this.horizontalDriftMax)}vw`, 2);
    // Random rotation for each flake
    this.renderer.setStyle(snowflake, '--rotation',
        `${this.random(this.rotationMin, this.rotationMax)}deg`, 2);

    // Randomly add lens flare effect to some snowflakes
    if (this.applyFlareEffect && Math.random() > 0.8) {
      this.renderer.addClass(snowflake, 'flare');
    }

    // Randomly blur some snowflakes to add depth
    if (this.applyBlurEffect && Math.random() > 0.5) {
      this.renderer.addClass(snowflake, 'blurred');
    }

    this.renderer.appendChild(this.elRef.nativeElement, snowflake);

    // Remove snowflake after it falls out of view including the listener
    this.elStore.set(snowflakeID, {
      el: snowflake,
      unlistener: this.renderer.listen(snowflake, 'animationend', () => {
        this.elStore.get(snowflakeID)?.unlistener();
        this.renderer.removeChild(this.elRef.nativeElement, snowflake);
        this.elStore.delete(snowflakeID);
      })
    })
  }

  /**
   * Start the snowfall animation.
   * @private
   */
  private startSnowfall() {
    if (!this.snowflakeIntervalRef) {
      this.snowflakeIntervalRef = setInterval(() => this.createSnowflake(),
          1000 / this.snowfallDensity );
    }
  }

  /**
   *  Stop the snowfall animation and remove all snowflakes at once.
    * @private
   */
  private stopSnowfall() {
    clearInterval(this.snowflakeIntervalRef);
    this.snowflakeIntervalRef = null;

    this.elStore.forEach((value) => {
      value.unlistener?.();
      this.renderer.removeChild(this.elRef.nativeElement, value.el);
    });

    this.elStore.clear();
  }

  /**
   * Set the timer that starts snowfall.
   * @private
   */
  private setInactivityTimer() {
    clearTimeout(this.inactivityTimeoutRef);
    this.inactivityTimeoutRef = setTimeout(() => {
        this.startSnowfall();

        if (this.showOverlay) {
          const overlayID = generateUUID();
          const overlay = this.renderer.createElement('div');

          this.renderer.addClass(overlay, 'snowfallOverlay');
          this.renderer.appendChild(this.elRef.nativeElement, overlay);

          this.elStore.set(overlayID, {el: overlay});
        }
      }, this.minToMs(this.inactivityTime));
  }

  /**
   * Function to generate a random number between min and max
   * @param min
   * @param max
   * @private
   */
  private random(min: number, max: number) {
    return Math.random() * (max - min) + min;
  }

  /**
   * Convert minutes to milliseconds.
   * @param minutes
   * @private
   */
  private minToMs(minutes: number) {
    return minutes * 60 * 1000;
  }

  /**
   * Run 'unlisteners' and remove all references from elStore.
   * Remove any scheduled snowfall.
   * @private
   */
  private cleanup() {
    this.stopSnowfall();
    clearTimeout(this.inactivityTimeoutRef);
  }

  /**
   * Make it stop snowing immediately and schedule next snowfall.
   * @private
   */
  private restartSnowfall() {
    this.stopSnowfall();
    this.setInactivityTimer();
  }

}
